import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DataSourceBadge } from "@/components/DataSourceBadge";
import { Loader2, TrendingDown, TrendingUp, Sparkles, Wand2, ExternalLink } from "lucide-react";

/**
 * Slide-over drawer that asks Claude to diagnose a keyword rank change
 * using the live SERP snapshot (competitors, AI Overview presence, etc.).
 */
export function RankDropExplainer({ open, onOpenChange, projectId, keywordId, keywordLabel }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!open || !projectId || !keywordId) return;
        setData(null);
        setErr(null);
        setLoading(true);
        client
            .get(`/projects/${projectId}/keywords/${keywordId}/explain`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(e?.response?.data?.detail || "Could not diagnose this keyword"))
            .finally(() => setLoading(false));
    }, [open, projectId, keywordId]);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                data-testid="rank-drop-explainer"
                side="right"
                className="w-full sm:max-w-xl overflow-y-auto bg-background border-l border-border"
            >
                <SheetHeader>
                    <div className="overline flex items-center gap-2">
                        <Wand2 className="w-3 h-3" /> AI RANK DIAGNOSIS
                        {data && <DataSourceBadge source={data.data_source === "live" ? "dataforseo" : "mock"} />}
                    </div>
                    <SheetTitle className="font-display text-2xl tracking-tight">
                        Why did “{keywordLabel || (data && data.keyword) || "this keyword"}” move?
                    </SheetTitle>
                    <SheetDescription className="text-muted-foreground text-sm">
                        Claude reads the live SERP + competitor snippets and diagnoses the drop.
                    </SheetDescription>
                </SheetHeader>

                {loading && (
                    <div className="mt-10 flex flex-col items-center justify-center gap-3 py-10" data-testid="explain-loading">
                        <Loader2 className="w-6 h-6 text-primary animate-spin" />
                        <div className="text-sm text-muted-foreground font-mono">Diagnosing…</div>
                    </div>
                )}

                {err && (
                    <div className="mt-6 text-sm text-destructive border border-destructive/40 bg-destructive/5 p-4" data-testid="explain-error">
                        {err}
                    </div>
                )}

                {data && !loading && (
                    <div className="mt-6 space-y-6" data-testid="explain-content">
                        {/* Position summary */}
                        <div className="grid grid-cols-3 gap-3">
                            <div className="border border-border p-4">
                                <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Now</div>
                                <div className="font-mono text-2xl mt-1">{data.current_position}</div>
                            </div>
                            <div className="border border-border p-4">
                                <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Was</div>
                                <div className="font-mono text-2xl mt-1 text-muted-foreground">{data.previous_position}</div>
                            </div>
                            <div className="border border-border p-4">
                                <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Change</div>
                                <div className={`font-mono text-2xl mt-1 flex items-center gap-1 ${data.change > 0 ? "text-success" : data.change < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                                    {data.change > 0 ? <TrendingUp className="w-5 h-5" /> : data.change < 0 ? <TrendingDown className="w-5 h-5" /> : null}
                                    {data.change > 0 ? "+" : ""}{data.change}
                                </div>
                            </div>
                        </div>

                        {/* SERP context */}
                        <div className="flex flex-wrap gap-2 text-xs">
                            {data.ai_overview && (
                                <span className="inline-flex items-center gap-1 border border-secondary/40 bg-secondary/5 text-secondary px-2 py-1">
                                    <Sparkles className="w-3 h-3" /> AI Overview present
                                </span>
                            )}
                            {(data.serp_features || []).map((f) => (
                                <span key={f} className="border border-border text-muted-foreground px-2 py-1">{f}</span>
                            ))}
                        </div>

                        {/* Competitors */}
                        {data.top_competitors?.length > 0 && (
                            <div>
                                <div className="overline mb-2">Currently ranking above</div>
                                <div className="border border-border divide-y divide-border">
                                    {data.top_competitors.map((c, i) => (
                                        <div key={i} className="p-3 flex items-start gap-3 text-sm">
                                            <div className="font-mono text-xs text-muted-foreground w-6 pt-0.5">#{c.position}</div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-mono text-xs text-primary truncate">{c.domain}</div>
                                                <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{c.title}</div>
                                            </div>
                                            {c.url && (
                                                <a href={c.url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary shrink-0">
                                                    <ExternalLink className="w-3.5 h-3.5" />
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* AI Diagnosis */}
                        <div>
                            <div className="overline mb-2">Claude's diagnosis</div>
                            {data.diagnosis ? (
                                <pre data-testid="explain-diagnosis"
                                     className="whitespace-pre-wrap font-sans text-sm text-foreground bg-card/40 border border-border p-5 leading-relaxed">
{data.diagnosis}
                                </pre>
                            ) : (
                                <div className="text-sm text-muted-foreground border border-border p-4">
                                    AI generation temporarily unavailable. {data.ai_error && <span className="text-destructive/80 block mt-2 font-mono text-xs">{data.ai_error}</span>}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
